// פאנלים משניים בעורך: היסטוריית גרסאות, בעיות ובדיקות, התאמה לגדלים והפצה, תצוגה מקדימה ב-sandbox.
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { familyOf, type BannerDoc } from '@bb/schema';
import { geometryIssues } from '@bb/renderer';
import { api, waitForJob, type Job } from '../../api';
import { ErrorBox, fmtDate, JobBadge, Loading, Modal, useToast } from '../ui';
import BannerView, { fitScale } from './BannerView';
import { FAMILY_LABEL, type AssetMap, type Check, type GeoIssue, type Sibling } from './types';

// ── גרסאות ──
interface RevRow { revision: number; source: string; summary: string; created_at: string; created_by: string | null }
const SOURCE_LABEL: Record<string, string> = { template: 'תבנית', manual: 'ידני', ai_generate: 'יצירת AI', ai_edit: 'עריכת AI', adapt: 'התאמת AI', revert: 'חזרה' };

export function VersionsDrawer({ variantId, revision, assets, flush, onReverted, onClose }: { variantId: string; revision: number; assets: AssetMap; flush: () => Promise<number | null>; onReverted: (rev: number, doc: BannerDoc) => void; onClose: () => void }) {
  const [rows, setRows] = useState<RevRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<{ revision: number; doc: BannerDoc } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get<RevRow[]>(`/api/variants/${variantId}/revisions`).then(setRows).catch((e) => setErr(e.message)); }, [variantId, revision]);
  const open = async (r: number) => { setErr(null); try { setView(await api.get(`/api/variants/${variantId}/revisions/${r}`)); } catch (e: any) { setErr(e.message); } };
  const revert = async (r: number) => {
    setBusy(true); setErr(null);
    try {
      const cur = await flush();
      if (cur === null) { setErr('יש שינויים שלא נשמרו או התנגשות — לא ניתן לחזור כעת.'); return; }
      const res = await api.post<{ revision: number; doc: BannerDoc }>(`/api/variants/${variantId}/revert`, { toRevision: r, expectedRevision: cur });
      onReverted(res.revision, res.doc);
      setView(null);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title="היסטוריית גרסאות" onClose={onClose} wide>
      <ErrorBox error={err} />
      <p className="small muted">כל גרסה נשמרת בשרת ואינה משתנה. חזרה לגרסה יוצרת גרסה חדשה עם התוכן הישן.</p>
      <div className="grid cols-2">
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
          {!rows ? <Loading /> : (
            <table className="table">
              <thead><tr><th>גרסה</th><th>מקור</th><th>תקציר</th><th>זמן</th><th><span className="sr-only">פעולות</span></th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.revision} aria-current={r.revision === revision ? 'true' : undefined}>
                    <td className="mono">{r.revision}{r.revision === revision && <span className="badge info" style={{ marginInlineStart: 4 }}>נוכחית</span>}</td>
                    <td>{SOURCE_LABEL[r.source] ?? r.source}</td>
                    <td className="small">{r.summary}</td>
                    <td className="small">{fmtDate(r.created_at)}</td>
                    <td><button className="btn small" onClick={() => open(r.revision)}>הצג</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          {view ? (
            <div className="stack">
              <strong>גרסה {view.revision}</strong>
              <BannerView doc={view.doc} assets={assets} autoplay scale={fitScale(view.doc.width, view.doc.height, 420, 360)} />
              <div className="row">
                <button className="btn primary" disabled={busy || view.revision === revision} onClick={() => revert(view.revision)}>חזרה לגרסה זו</button>
              </div>
            </div>
          ) : <div className="empty">בחרו גרסה לתצוגה</div>}
        </div>
      </div>
    </Modal>
  );
}

// ── בעיות ובדיקות ──
export function IssuesPanel({ variantId, doc, kit, flush, onSelectLayer }: { variantId: string; doc: BannerDoc; kit: { minFontSizePx: number; logoMinWidthPx: number | null }; flush: () => Promise<number | null>; onSelectLayer: (id: string) => void }) {
  // בדיקות גאומטריה חיות — אותה פונקציה שהשרת מריץ
  const live: GeoIssue[] = useMemo(() => geometryIssues(doc, { minFontPx: kit.minFontSizePx, logoMinWidthPx: kit.logoMinWidthPx }) as GeoIssue[], [doc, kit]);
  const [checks, setChecks] = useState<{ checks: Check[]; summary: any } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const validate = async () => {
    setBusy(true); setErr(null);
    try {
      if ((await flush()) === null) { setErr('יש שינויים שלא נשמרו — הבדיקה תרוץ על הגרסה השמורה האחרונה.'); }
      setChecks(await api.post(`/api/variants/${variantId}/validate`, {}));
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };
  const failed = checks?.checks.filter((c) => !c.passed) ?? [];
  return (
    <section className="ed-panel" aria-labelledby="iss-h">
      <h3 id="iss-h" className="ed-h">בעיות <span className={`badge ${live.some((i) => i.severity === 'error') ? 'err' : live.length ? 'warn' : 'ok'}`}>{live.length}</span></h3>
      {live.length === 0 ? <div className="small" style={{ color: 'var(--ok)' }}>אין גלישה, חפיפה, טקסט קטן או חיתוך לוגו.</div> : (
        <ul className="iss-list">
          {live.map((i, k) => (
            <li key={k}><button className="linkish" onClick={() => i.layerIds[0] && onSelectLayer(i.layerIds[0])}>
              <span className={`badge ${i.severity === 'error' ? 'err' : 'warn'}`}>{i.severity === 'error' ? 'שגיאה' : 'אזהרה'}</span> {i.message}
            </button></li>
          ))}
        </ul>
      )}
      <div className="row" style={{ marginTop: 6 }}>
        <button className="btn small" onClick={validate} disabled={busy}>{busy ? 'בודק…' : 'הרץ בדיקות מלאות'}</button>
        {checks && <span className="small muted">{checks.summary?.errors ?? 0} שגיאות · {checks.summary?.warnings ?? 0} אזהרות · {checks.summary?.passed ?? 0} עברו</span>}
      </div>
      <ErrorBox error={err} />
      {checks && failed.length > 0 && (
        <ul className="iss-list">
          {failed.map((c) => <li key={c.code}><span className={`badge ${c.severity === 'error' ? 'err' : c.severity === 'warning' ? 'warn' : 'info'}`}>{c.severity}</span> {c.message} <span className="mono muted small">{c.code}</span></li>)}
        </ul>
      )}
    </section>
  );
}

// ── התאמה לגדלים והפצה ──
const ROLES = [['headline', 'כותרת'], ['subline', 'משנה'], ['cta', 'CTA'], ['disclaimer', 'גילוי נאות'], ['logo', 'לוגו'], ['image', 'תמונה']] as const;
const WHAT = [['text', 'טקסט'], ['asset', 'נכס (תמונה/לוגו)'], ['color', 'צבע']] as const;

export function AdaptPanel({ variantId, doc, siblings, revision, flush, onAdopt }: { variantId: string; doc: BannerDoc; siblings: Sibling[]; revision: number; flush: () => Promise<number | null>; onAdopt: (rev: number, doc: BannerDoc) => void }) {
  const toast = useToast();
  const others = siblings.filter((s) => s.id !== variantId);
  const myFam = familyOf(doc.width, doc.height);
  const [jobs, setJobs] = useState<Record<string, Job | { status: string; error?: string }>>({});
  const [targets, setTargets] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>(['headline', 'subline', 'cta']);
  const [what, setWhat] = useState<string[]>(['text']);
  const [prop, setProp] = useState<{ variantId: string; revision?: number; error?: string }[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const adapt = async (targetId: string, fromId: string) => {
    setErr(null);
    if ((await flush()) === null) { setErr('יש שינויים שלא נשמרו — ההתאמה תתבסס על הגרסה השמורה. שמרו ונסו שוב.'); return; }
    const key = `${targetId}<${fromId}`;
    setJobs((j) => ({ ...j, [key]: { status: 'queued' } }));
    try {
      const { jobId } = await api.post<{ jobId: string }>(`/api/variants/${targetId}/adapt`, { fromVariantId: fromId, idempotencyKey: crypto.randomUUID() });
      const j = await waitForJob(jobId, (x) => setJobs((m) => ({ ...m, [key]: x })));
      if (j.status === 'succeeded' && targetId === variantId) {
        const cur = await api.get<{ revision: { revision: number; doc: BannerDoc } }>(`/api/variants/${variantId}`);
        if (cur.revision) onAdopt(cur.revision.revision, cur.revision.doc);
      }
      if (j.status === 'succeeded') toast('ההתאמה הסתיימה ונשמרה כגרסה חדשה');
    } catch (e: any) { setJobs((m) => ({ ...m, [key]: { status: 'failed', error: e.message } })); }
  };

  const propagate = async () => {
    setBusy(true); setErr(null); setProp(null);
    try {
      if ((await flush()) === null) { setErr('יש שינויים שלא נשמרו — שמרו ונסו שוב.'); return; }
      const r = await api.post<{ results: { variantId: string; revision?: number; error?: string }[] }>(`/api/variants/${variantId}/propagate`, { targetVariantIds: targets, roles, what });
      setProp(r.results);
    } catch (e: any) { setErr(e.message); } finally { setBusy(false); }
  };

  const toggle = (arr: string[], v: string, set: (x: string[]) => void) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const nameOf = (id: string) => others.find((s) => s.id === id)?.display_name ?? id;

  return (
    <section className="ed-panel" aria-labelledby="adapt-h">
      <h3 id="adapt-h" className="ed-h">גדלים אחרים בסט</h3>
      <p className="small muted">תיקון בגודל זה הוא מקומי. כדי להעביר אותו לגדלים אחרים יש לבחור במפורש מה להפיץ. מעבר למשפחת יחס ממדים אחרת נעשה בהתאמת AI עם אילוצים — קומפוזיציה נפרדת, לא הקטנה.</p>
      <ErrorBox error={err} />
      {others.length === 0 ? <div className="small muted">אין גדלים נוספים בסט.</div> : (
        <ul className="sib-list">
          {others.map((s) => {
            const fam = familyOf(s.width, s.height);
            const kTo = `${s.id}<${variantId}`, kFrom = `${variantId}<${s.id}`;
            return (
              <li key={s.id}>
                <div className="row between">
                  <Link to={`/variants/${s.id}/edit`} className="mono">{s.width}×{s.height}</Link>
                  <span className="small muted">{FAMILY_LABEL[fam]}{fam === myFam ? ' (אותה משפחה)' : ''}</span>
                </div>
                <div className="row" style={{ gap: 4 }}>
                  <button className="btn small" onClick={() => adapt(s.id, variantId)} title="יוצר קומפוזיציה חדשה לגודל היעד לפי הבאנר הזה">התאם את {s.width}×{s.height} מגודל זה (AI)</button>
                  <button className="btn small" onClick={() => adapt(variantId, s.id)}>התאם גודל זה ממנו (AI)</button>
                </div>
                {[kTo, kFrom].map((k) => jobs[k] && <div key={k} className="small"><JobBadge status={jobs[k].status} /> {(jobs[k] as any).error ?? ''}</div>)}
              </li>
            );
          })}
        </ul>
      )}
      {others.length > 0 && (
        <fieldset className="pgroup">
          <legend>הפצת שינויים לגדלים אחרים</legend>
          <div className="small muted">יעדים</div>
          <div className="row">{others.map((s) => <label key={s.id} className="chk"><input type="checkbox" checked={targets.includes(s.id)} onChange={() => toggle(targets, s.id, setTargets)} /> <span className="mono">{s.width}×{s.height}</span></label>)}</div>
          <div className="small muted">תפקידים</div>
          <div className="row">{ROLES.map(([r, t]) => <label key={r} className="chk"><input type="checkbox" checked={roles.includes(r)} onChange={() => toggle(roles, r, setRoles)} /> {t}</label>)}</div>
          <div className="small muted">מה להפיץ</div>
          <div className="row">{WHAT.map(([w, t]) => <label key={w} className="chk"><input type="checkbox" checked={what.includes(w)} onChange={() => toggle(what, w, setWhat)} /> {t}</label>)}</div>
          <button className="btn small primary" disabled={busy || !targets.length || !roles.length || !what.length} onClick={propagate}>הפץ (גרסה חדשה בכל יעד)</button>
          {prop && <ul className="small">{prop.map((r) => <li key={r.variantId}>{nameOf(r.variantId)}: {r.error ? <span style={{ color: 'var(--danger)' }}>{r.error}</span> : <>נשמר כגרסה <span className="mono">{r.revision}</span></>}</li>)}</ul>}
        </fieldset>
      )}
      <div className="small muted">גרסה נוכחית: <span className="mono">{revision}</span></div>
    </section>
  );
}

// ── תצוגה מקדימה ב-sandbox ──
export function PreviewModal({ variantId, revision, width, height, onClose }: { variantId: string; revision: number; width: number; height: number; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    setErr(null);
    api.get<{ url: string }>(`/api/variants/${variantId}/preview-url?revision=${revision}`).then((r) => setUrl(r.url)).catch((e) => setErr(e.message));
  }, [variantId, revision]);
  return (
    <Modal title={`תצוגה מקדימה — גרסה ${revision}`} onClose={onClose} wide>
      <ErrorBox error={err} />
      <p className="small muted">הבאנר רץ מ-origin מבודד, באותו renderer של היצוא, ב-iframe בגודל המדויק <span className="mono">{width}×{height}</span>.</p>
      <div className="row" style={{ marginBottom: 8 }}>
        <button className="btn small" onClick={() => setN((x) => x + 1)} disabled={!url}>↻ טעינה מחדש / ניגון מההתחלה</button>
        {url && <span className="mono small muted" style={{ wordBreak: 'break-all' }}>{new URL(url, location.href).origin}</span>}
      </div>
      <div className="preview-wrap">
        {url ? <iframe key={n} title={`תצוגה מקדימה ${width}x${height}`} src={url} width={width} height={height} style={{ width, height, border: 0, display: 'block' }} sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox" /> : !err && <Loading />}
      </div>
    </Modal>
  );
}
